import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  fetchMidpointMediaCrmSummary,
  fetchMidpointMediaDigitalOcean,
  fetchMidpointMediaHealth,
  fetchMidpointMediaOpenProject,
} from "../../../api/midpointMediaAdminApi";
import {
  ClientPortalError,
  ClientPortalLoading,
} from "../../../components/clientPortal/ClientPortalStates";
import useMidpointMediaAdminView from "../../../hooks/useMidpointMediaAdminView";
import "../../../styles/midpointMediaAdmin.css";

function statusBadge(live, configured) {
  if (live) return <span className="mm-admin-badge mm-admin-badge--live">Live</span>;
  if (configured) return <span className="mm-admin-badge mm-admin-badge--warn">Check config</span>;
  return <span className="mm-admin-badge mm-admin-badge--demo">Demo</span>;
}

export default function MidpointMediaAdminDashboardPage() {
  const { clientViewId, paths, viewBase } = useMidpointMediaAdminView();
  const [state, setState] = useState({ loading: true, error: "", data: null });

  useEffect(() => {
    if (!clientViewId) {
      setState({ loading: false, error: "", data: null });
      return undefined;
    }
    let cancelled = false;
    setState({ loading: true, error: "", data: null });
    Promise.allSettled([
      fetchMidpointMediaCrmSummary(clientViewId),
      fetchMidpointMediaHealth(clientViewId),
      fetchMidpointMediaDigitalOcean(clientViewId),
      fetchMidpointMediaOpenProject(clientViewId),
    ]).then(([crm, health, droplet, projects]) => {
      if (cancelled) return;
      const failed = [crm, health, droplet, projects].find(
        (entry) => entry.status === "rejected"
      );
      setState({
        loading: false,
        error: failed?.reason?.message || "",
        data: {
          crm: crm.status === "fulfilled" ? crm.value : null,
          health: health.status === "fulfilled" ? health.value : null,
          droplet: droplet.status === "fulfilled" ? droplet.value : null,
          projects: projects.status === "fulfilled" ? projects.value : null,
        },
      });
    });
    return () => {
      cancelled = true;
    };
  }, [clientViewId]);

  if (!clientViewId) {
    return (
      <p className="empty-state">No Midpoint Media workspace is assigned to this account.</p>
    );
  }
  if (state.loading) return <ClientPortalLoading message="Loading operations overview…" />;
  if (!state.data && state.error) return <ClientPortalError message={state.error} />;

  const summary = state.data?.health?.summary || {};
  const crm = state.data?.crm?.summary || {};
  const dropletLive = Boolean(state.data?.droplet?.live);
  const projectsLive = Boolean(state.data?.projects?.live);

  return (
    <div className="mm-admin-page">
      <header>
        <p className="mm-admin-kicker">Midpoint Media · Operations</p>
        <h1>Administrative workspace</h1>
        <p className="mm-admin-lead">
          CRM, people, production health, and OpenProject delivery — without the real-estate
          community tools from other client sites.
        </p>
      </header>

      <section className="mm-admin-grid" aria-label="Workspace snapshot">
        <article className="mm-admin-card">
          <h3>CRM leads</h3>
          <div className="mm-admin-kpi">
            {crm.leads?.total ?? "—"}
            <span>In this workspace</span>
          </div>
        </article>
        <article className="mm-admin-card">
          <h3>Opportunities</h3>
          <div className="mm-admin-kpi">
            {crm.opportunities?.total ?? "—"}
            <span>Open pipeline records</span>
          </div>
        </article>
        <article className="mm-admin-card">
          <h3>API host</h3>
          <div className="mm-admin-kpi">
            {summary.status || "—"}
            <span>{summary.uptimeLabel || "Application health"}</span>
          </div>
        </article>
        <article className="mm-admin-card">
          <h3>Projects</h3>
          <div className="mm-admin-kpi">
            {state.data?.projects?.totals?.projects ??
              state.data?.projects?.demo?.totals?.projects ??
              "—"}
            <span>{projectsLive ? "OpenProject connected" : "Demo until configured"}</span>
          </div>
        </article>
      </section>

      <section className="mm-admin-grid" style={{ gridTemplateColumns: "1fr 1fr" }}>
        <article className="mm-admin-card">
          <h2>Work from here</h2>
          <div className="mm-admin-actions">
            <Link className="btn btn-primary" to={`${paths.base}/crm`}>
              Open CRM
            </Link>
            {viewBase ? (
              <Link className="btn btn-secondary" to={`${viewBase}/opportunities`}>
                Opportunities
              </Link>
            ) : null}
            <Link className="btn btn-secondary" to={`${paths.base}/people`}>
              People
            </Link>
            <Link className="btn btn-secondary" to={`${paths.base}/infrastructure`}>
              Server health
            </Link>
            <Link className="btn btn-secondary" to={`${paths.base}/projects`}>
              OpenProject
            </Link>
          </div>
        </article>
        <article className="mm-admin-card">
          <h2>Integrations</h2>
          <p>
            DigitalOcean droplet {statusBadge(dropletLive, state.data?.droplet?.configured)}
          </p>
          <p>
            OpenProject {statusBadge(projectsLive, state.data?.projects?.configured)}
          </p>
          <p className="mm-admin-lead">
            Application health is live from this API process. Droplet metrics and OpenProject
            need tokens — each page includes setup steps.
          </p>
        </article>
      </section>
    </div>
  );
}
